import { itx } from "../../../src/translationItx/itx";
import { useState, type CSSProperties } from "react";
import { furnitureById } from "./C1_furnitureRegistry";
import c1_state from "../model/C1_State";
import { FurnitureCategory } from "../types/C1_ModelTypes";
import { darkenHexColor } from "../lib/C1_color";
import { C1_FURNISHING_FURNITURE_DARKEN_FACTOR, C1_FURNISHING_FURNITURE_FILL_COLOR } from "../model/C1_ModelSettings";
import { C1_FurnitureVectorContext } from "./C1_FurnitureSvg";
import { C1_SvgResizeGrab, type C1_ResizeGrabDirection } from "../components/C1_ResizeGrab";
import { getEloItemProfile } from "../data/C1_eloItemsRegistry";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import type { C1_FurnitureProps, C1_FurnitureRenderData } from "../types/C1_FurnitureTypes";
import { isLightGroupDefinitionId } from "./C1_lightGroup";
import { C1_OrangeSelectionFilter } from "../plan/C1_OrangeSelectionFilter";

function isElectricalFurniture(definition: (typeof furnitureById)[string] | undefined) {
  return Boolean(
    definition &&
      (definition.category === FurnitureCategory.Electrical ||
        definition.additionalCategories?.includes(FurnitureCategory.Electrical)),
  );
}

export interface C1_FurnitureRendererProps {
  PX: number;
  furniture: C1_FurnitureRenderData;
  isSelected: boolean;
  indirectlySelected?: boolean;
  ghost?: boolean;
}

function masterIndexPosition(definitionId: string): { x: number; y: number } | undefined {
  return getEloItemProfile(definitionId).find((definition) => definition.masterIndexPosition)?.masterIndexPosition;
}

function resizeCursorForAngle(angle: number): CSSProperties["cursor"] {
  const direction = ((Math.round(angle / 45) % 4) + 4) % 4;
  return (["ew-resize", "nwse-resize", "ns-resize", "nesw-resize"] as const)[direction];
}

function C1_FurnitureDimensions({
  furniture,
  x,
  y,
  width,
  height,
  rotation,
  onSetDimension,
  onEditingChange,
  showWidth = true,
  showHeight = true,
}: {
  furniture: C1_FurnitureRenderData;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  onSetDimension: (axis: "width" | "height", value: number) => void;
  onEditingChange: (editing: boolean) => void;
  showWidth?: boolean;
  showHeight?: boolean;
}) {
  const [editing, setEditing] = useState<"width" | "height" | null>(null);
  const [inputValue, setInputValue] = useState("");
  const offset = 38;
  const tick = 6;
  const horizontalY = y - offset;
  const verticalX = x - offset;
  const numberLocale = state.settings.uiLanguage;
  const formatDimension = (value: number) =>
    new Intl.NumberFormat(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

  const startEditing = (axis: "width" | "height") => {
    setEditing(axis);
    setInputValue(furniture.baseProps[axis].toFixed(2));
    onEditingChange(true);
  };

  const commit = (axis: "width" | "height") => {
    const value = Number.parseFloat(inputValue.replace(",", "."));
    if (Number.isFinite(value) && value >= 0.1) onSetDimension(axis, Math.round(value * 100) / 100);
    setEditing(null);
    onEditingChange(false);
  };

  const label = (axis: "width" | "height", value: number, labelX: number, labelY: number, localAngle = 0) => {
    const globalAngle = (((rotation + localAngle) % 360) + 360) % 360;
    const readableGlobalAngle = globalAngle > 90 && globalAngle < 270 ? globalAngle - 180 : globalAngle;
    const displayAngle = readableGlobalAngle - rotation;
    const displayTransform = displayAngle ? `rotate(${displayAngle} ${labelX} ${labelY})` : undefined;
    const inputTransform = rotation ? `rotate(${-rotation} ${labelX} ${labelY})` : undefined;

    if (editing === axis) {
      const inputWidth = 76;
      const inputHeight = 28;
      return (
        <foreignObject
          x={labelX - inputWidth / 2}
          y={labelY - inputHeight / 2}
          width={inputWidth}
          height={inputHeight}
          overflow="visible"
          transform={inputTransform}
        >
          <input
            // @ts-expect-error xmlns is required for HTML inside SVG foreignObject
            xmlns="http://www.w3.org/1999/xhtml"
            type="number"
            min="0.1"
            step="0.01"
            value={inputValue}
            className="C1_dimensionInput"
            autoFocus
            onChange={(event) => setInputValue(event.target.value)}
            onBlur={() => commit(axis)}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter") event.currentTarget.blur();
              if (event.key === "Escape") {
                setEditing(null);
                onEditingChange(false);
              }
            }}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          />
        </foreignObject>
      );
    }

    return (
      <g
        className="C1_dimensionLabel"
        transform={displayTransform}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          startEditing(axis);
        }}
      >
        <rect x={labelX - 27} y={labelY - 10} width={54} height={18} rx={5} />
        <text x={labelX} y={labelY} textAnchor="middle" dominantBaseline="central">
          {formatDimension(value)}
        </text>
      </g>
    );
  };

  return (
    <g className="C1_dimensions" data-c1-svg-layer="furniture-dimensions" onDoubleClick={(event) => event.stopPropagation()}>
      {showWidth && (
        <g className="C1_dimensionGroup">
          <line x1={x} y1={y} x2={x} y2={horizontalY + tick} />
          <line x1={x + width} y1={y} x2={x + width} y2={horizontalY + tick} />
          <line x1={x} y1={horizontalY} x2={x + width} y2={horizontalY} />
          <line x1={x} y1={horizontalY - tick} x2={x} y2={horizontalY + tick} />
          <line x1={x + width} y1={horizontalY - tick} x2={x + width} y2={horizontalY + tick} />
          {label("width", furniture.baseProps.width, x + width / 2, horizontalY - 8)}
        </g>
      )}
      {showHeight && (
        <g className="C1_dimensionGroup">
          <line x1={x} y1={y} x2={verticalX + tick} y2={y} />
          <line x1={x} y1={y + height} x2={verticalX + tick} y2={y + height} />
          <line x1={verticalX} y1={y} x2={verticalX} y2={y + height} />
          <line x1={verticalX - tick} y1={y} x2={verticalX + tick} y2={y} />
          <line x1={verticalX - tick} y1={y + height} x2={verticalX + tick} y2={y + height} />
          {label("height", furniture.baseProps.height, verticalX - 8, y + height / 2, -90)}
        </g>
      )}
    </g>
  );
}

export function C1_FurnitureRenderer({ isSelected, PX, furniture }: C1_FurnitureRendererProps) {
  useOnChangeWatcher(c1_state.settings);
  const resolved = resolveFurnitureRender(PX, furniture, isSelected);
  if (!resolved) {
    return <div>{itx("c1.ui.Unbekanntes_Mobelstuck", "Unbekanntes Möbelstück:")} {furniture.definitionId}</div>;
  }
  const { Renderer, props, rotation, reflect } = resolved;
  const definition = furnitureById[furniture.definitionId];
  const originXRatio = (definition?.originXPercentage ?? 0) / 100;
  const originYRatio = (definition?.originYPercentage ?? 0) / 100;
  const configuredMasterIndexPosition = masterIndexPosition(furniture.definitionId);
  const technicalMode =
    (c1_state.floorImageRenderOptions.value?.displayMode ?? c1_state.project.displayMode.value) !== "design";
  const showMasterIndex = !(technicalMode && isElectricalFurniture(definition));

  return (
    <>
      <div
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          transform: `rotate(${rotation}deg) scaleX(${reflect ? -1 : 1})`,
          transformOrigin: "0 0",
        }}
      >
        <Renderer {...props} />
      </div>
      {c1_state.settings.showTags &&
        showMasterIndex &&
        (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") &&
        furniture.masterIndex !== undefined && (
          <span
            className="C1_masterIndexLabel"
            style={{
              left:
                (configuredMasterIndexPosition?.x ?? furniture.baseProps.width / 2) * PX -
                furniture.baseProps.width * PX * originXRatio,
              top:
                (configuredMasterIndexPosition?.y ?? furniture.baseProps.height / 2) * PX -
                furniture.baseProps.height * PX * originYRatio,
            }}
          >
            {furniture.masterIndex}
          </span>
        )}
    </>
  );
}

function resolveFurnitureRender(PX: number, furniture: C1_FurnitureRenderData, isSelected: boolean, indirectlySelected = false) {
  const definition = furnitureById[furniture.definitionId];

  if (!definition) return undefined;

  const pp: C1_FurnitureProps & { PX: number } = { ...furniture.baseProps, PX };
  pp.eloTree = furniture.eloTree;
  pp.wallDocked = furniture.wallDocked !== undefined;
  pp.width *= PX;
  pp.height *= PX;
  pp.PX = PX;
  pp.fillColor ||= c1_state.project.settings.furnitureFillColor;
  pp.strokeColor ||= c1_state.project.settings.furnitureStrokeColor;
  pp.strokeWidth ||= c1_state.project.settings.furnitureStrokeWidth;
  if (c1_state.editMode.value === "furnishing") {
    pp.fillColor = C1_FURNISHING_FURNITURE_FILL_COLOR;
    pp.strokeColor = darkenHexColor(pp.strokeColor, C1_FURNISHING_FURNITURE_DARKEN_FACTOR);
  }
  if (isSelected) {
    pp.strokeColor = "orange";
  } else if (indirectlySelected) {
    pp.strokeColor = "#ffc766";
  }
  pp.showDetails = true;
  const rotation = Number(pp.rotation) || 0;
  const reflect = (definition.autoReflect || definition.allowManualReflect) && furniture.reflect === true;
  pp.ownerRotation = rotation;
  pp.ownerReflect = reflect;
  pp.rotation = 0;
  return { Renderer: definition.Renderer, props: pp, rotation, reflect };
}

export function C1_FurnitureSvgRenderer({
  isSelected,
  indirectlySelected = false,
  ghost = false,
  PX,
  furniture,
  x,
  y,
  interactive = true,
  onPointerDown,
  onDoubleClick,
  onStartResize,
  onSetDimension,
}: C1_FurnitureRendererProps & {
  x: number;
  y: number;
  interactive?: boolean;
  onPointerDown?: (event: React.PointerEvent<SVGGElement>) => void;
  onDoubleClick?: (event: React.MouseEvent<SVGGElement>) => void;
  onStartResize?: (event: React.PointerEvent<SVGGElement>, axis: "width" | "height", edge: "start" | "end") => void;
  onSetDimension?: (axis: "width" | "height", value: number) => void;
}) {
  useOnChangeWatcher(c1_state.settings);
  const [editingDimension, setEditingDimension] = useState(false);
  const selectionFilterId = `c1-furniture-selection-${furniture.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const resolved = resolveFurnitureRender(PX, furniture, isSelected, indirectlySelected);
  const width = furniture.baseProps.width * PX;
  const height = furniture.baseProps.height * PX;
  const label = String(furniture.masterIndex ?? "");
  const labelWidth = Math.max(22, label.length * 6 + 10);
  const subtleMasterIndex = c1_state.floorImageRenderOptions.value?.subtleMasterIndices === true;
  const masterIndexBackground = subtleMasterIndex ? "#e5e7eb" : "#173f56";
  const masterIndexColor = subtleMasterIndex ? "#64748b" : "#fff";

  if (!resolved) {
    return (
      <text x={x} y={y} fontSize={10} fill="#991b1b">
        {(itx("components.Unknown", "Unbekannt") + ":")} {furniture.definitionId}
      </text>
    );
  }
  const { Renderer, props, rotation, reflect } = resolved;
  const definition = furnitureById[furniture.definitionId];
  const originXRatio = (definition?.originXPercentage ?? 0) / 100;
  const originYRatio = (definition?.originYPercentage ?? 0) / 100;
  const furnitureX = -width * originXRatio;
  const furnitureY = -height * originYRatio;
  const furnitureCenterX = furnitureX + width / 2;
  const furnitureCenterY = furnitureY + height / 2;
  const configuredMasterIndexPosition = masterIndexPosition(furniture.definitionId);
  const masterIndexX = furnitureX + (configuredMasterIndexPosition?.x ?? furniture.baseProps.width / 2) * PX;
  const masterIndexY = furnitureY + (configuredMasterIndexPosition?.y ?? furniture.baseProps.height / 2) * PX;
  const displayedFurnitureX = reflect ? -furnitureX - width : furnitureX;
  const widthResizeCursor = resizeCursorForAngle(rotation);
  const heightResizeCursor = resizeCursorForAngle(rotation + 90);
  const wallDocked = furniture.wallDocked;
  const electricalFurniture = isElectricalFurniture(definition);
  const technicalMode =
    (c1_state.floorImageRenderOptions.value?.displayMode ?? c1_state.project.displayMode.value) !== "design";
  const furnitureSelected = isSelected || (electricalFurniture && indirectlySelected);
  const allowsFurnitureControls = (c1_state.editMode.value !== "EloConcept" && c1_state.editMode.value !== "EloDetail") || electricalFurniture;
  const showGrabs =
    definition?.editableGeometry !== false && furnitureSelected && interactive && allowsFurnitureControls;
  const showResizeGrabs = showGrabs && !editingDimension;
  const hasDefaultFarEdgeGrabs = definition?.autoReflect === true && originXRatio === 0 && originYRatio === 0;
  const capabilityContext = { values: furniture.baseProps, wallDocked: wallDocked !== undefined };
  const resolveCapability = (capability: typeof definition.grabsW) =>
    typeof capability === "function" ? capability(capabilityContext) : capability === true;
  const showsWidthGrabs = resolveCapability(definition?.grabsW) || hasDefaultFarEdgeGrabs;
  const showsHeightGrabs =
    definition?.grabsH !== false && (resolveCapability(definition?.grabsH) || hasDefaultFarEdgeGrabs);
  const grabOffset = 16;
  const widthGrabs: Array<{ edge: "start" | "end"; x: number; y: number }> =
    !showResizeGrabs || !showsWidthGrabs
      ? []
      : definition.autoReflect
        ? [{ edge: "end", x: furnitureX + width + grabOffset, y: furnitureCenterY }]
        : [
            { edge: "start", x: furnitureX - grabOffset, y: furnitureCenterY },
            { edge: "end", x: furnitureX + width + grabOffset, y: furnitureCenterY },
          ];
  const heightGrabs: Array<{ edge: "start" | "end"; x: number; y: number }> =
    !showResizeGrabs || !showsHeightGrabs
      ? []
      : wallDocked || hasDefaultFarEdgeGrabs
        ? [{ edge: "end", x: furnitureCenterX, y: furnitureY + height + grabOffset }]
        : [
            { edge: "start", x: furnitureCenterX, y: furnitureY - grabOffset },
            { edge: "end", x: furnitureCenterX, y: furnitureY + height + grabOffset },
          ];
  const widthGrabDirection = (edge: "start" | "end"): C1_ResizeGrabDirection => (edge === "start" ? "left" : "right");
  const heightGrabDirection = (edge: "start" | "end"): C1_ResizeGrabDirection => (edge === "start" ? "top" : "bottom");

  return (
    <g
      focusable="false"
      className={`C1_svgFurniture${isSelected ? " C1_selected" : ""}${indirectlySelected ? " C1_indirectlySelected" : ""}${ghost ? " C1_ghostFurniture" : ""}`}
      data-c1-furniture-id={furniture.id}
      transform={`translate(${x} ${y}) rotate(${rotation}) scale(${reflect ? -1 : 1} 1)`}
      style={{
        cursor: interactive ? (furnitureSelected && allowsFurnitureControls ? "move" : "default") : undefined,
        pointerEvents: interactive ? (isLightGroupDefinitionId(furniture.definitionId) ? "none" : "all") : "none",
      }}
      onPointerDown={interactive ? onPointerDown : undefined}
      onDoubleClick={interactive ? onDoubleClick : undefined}
    >
      {isSelected && (
        <defs>
          <C1_OrangeSelectionFilter id={selectionFilterId} />
        </defs>
      )}
      <g filter={isSelected ? `url(#${selectionFilterId})` : undefined}>
        <C1_FurnitureVectorContext.Provider value>
          <Renderer {...props} />
        </C1_FurnitureVectorContext.Provider>
      </g>
      {showGrabs && onSetDimension && (
        <g transform={reflect ? "scale(-1 1)" : undefined}>
          <C1_FurnitureDimensions
            furniture={furniture}
            x={displayedFurnitureX}
            y={furnitureY}
            width={width}
            height={height}
            rotation={rotation}
            onSetDimension={onSetDimension}
            onEditingChange={setEditingDimension}
            showWidth={isLightGroupDefinitionId(furniture.definitionId) ? showsWidthGrabs : true}
            showHeight={definition.grabsH !== false && (isLightGroupDefinitionId(furniture.definitionId) ? showsHeightGrabs : true)}
          />
        </g>
      )}
      {c1_state.settings.showTags &&
        !(technicalMode && electricalFurniture) &&
        (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") &&
        furniture.masterIndex !== undefined && (
          <g
            pointerEvents="none"
            transform={`translate(${masterIndexX} ${masterIndexY}) scale(${reflect ? -1 : 1} 1) rotate(${-rotation})`}
          >
            <rect x={-labelWidth / 2} y={-9} width={labelWidth} height={18} rx={9} fill={masterIndexBackground} />
            <text
              x={0}
              y={0}
              fill={masterIndexColor}
              fontSize={10}
              fontWeight={400}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {label}
            </text>
          </g>
        )}
      <rect
        data-c1-furniture-hitbox
        x={furnitureX}
        y={furnitureY}
        width={width}
        height={height}
        fill="transparent"
        stroke="none"
        pointerEvents={isLightGroupDefinitionId(furniture.definitionId) ? "none" : undefined}
      >
        <title>{furnitureById[furniture.definitionId]?.description[c1_state.language] ?? furniture.definitionId}</title>
      </rect>
      {widthGrabs.map((grab) => (
        <C1_SvgResizeGrab
          sizeScale={1 / (c1_state.zoom?.value ?? 1)}
          key={`width-${grab.edge}`}
          className="C1_svgFurnitureResizeGrab"
          data-c1-svg-layer="furniture-resize-grab"
          direction={widthGrabDirection(grab.edge)}
          x={grab.x}
          y={grab.y}
          style={{ cursor: widthResizeCursor }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onStartResize?.(event, "width", grab.edge);
          }}
        />
      ))}
      {heightGrabs.map((grab) => (
        <C1_SvgResizeGrab
          sizeScale={1 / (c1_state.zoom?.value ?? 1)}
          key={`height-${grab.edge}`}
          className="C1_svgFurnitureResizeGrab"
          data-c1-svg-layer="furniture-resize-grab"
          direction={heightGrabDirection(grab.edge)}
          x={grab.x}
          y={grab.y}
          style={{ cursor: heightResizeCursor }}
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onStartResize?.(event, "height", grab.edge);
          }}
        />
      ))}
    </g>
  );
}
